import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import {
  locationDiscountsGetAllByLocationId,
  locationsUpdateDiscountsSettings,
} from "~/api-refs";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "~/components/ui/form";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/locations/$id/discounts/settings"
)({
  component: DiscountSettingsModal,
});

interface DiscountsSettings {
  limitDiscountsToOrder: boolean;
  limitDiscountsToItem: boolean;
  enableOneTimeDiscount: boolean;
}

function DiscountSettingsModal() {
  const { id: locationId } = Route.useParams();
  const navigate = useNavigate();

  const { data, isPending } = useQuery(
    convexQuery(locationDiscountsGetAllByLocationId, {
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const form = useForm<DiscountsSettings>({
    defaultValues: {
      limitDiscountsToOrder: false,
      limitDiscountsToItem: false,
      enableOneTimeDiscount: false,
    },
  });

  // Sync form with data when it loads
  useEffect(() => {
    if (data?.settings) {
      form.reset({
        limitDiscountsToOrder: data.settings.limitDiscountsToOrder,
        limitDiscountsToItem: data.settings.limitDiscountsToItem,
        enableOneTimeDiscount: data.settings.enableOneTimeDiscount,
      });
    }
  }, [data?.settings, form]);

  const updateDiscountSettingsMutation = useMutation({
    mutationFn: (mutationData: {
      locationId: Id<"locations">;
      limitDiscountsToOrder: boolean;
      limitDiscountsToItem: boolean;
      enableOneTimeDiscount: boolean;
    }) => {
      return convexClient.mutation(
        locationsUpdateDiscountsSettings,
        mutationData
      );
    },
  });

  const limitDiscountsToOrder = form.watch("limitDiscountsToOrder");

  useEffect(() => {
    if (limitDiscountsToOrder) {
      form.setValue("limitDiscountsToItem", false);
    }
  }, [form, limitDiscountsToOrder]);

  const handleClose = () => {
    void navigate({
      to: "/locations/$id/discounts",
      params: { id: locationId },
    });
  };

  const onSubmit = async (values: DiscountsSettings) => {
    if (updateDiscountSettingsMutation.isPending) {
      return;
    }

    try {
      await updateDiscountSettingsMutation.mutateAsync({
        locationId: toConvexTableId("locations", locationId),
        ...values,
      });
      ProductEvents.settingsUpdated("discounts");
      handleClose();
    } catch (error) {
      logger.exception("Failed to update discount settings", error);
    }
  };

  if (isPending || !data?.settings) {
    return (
      <ModalLoadingDialog
        message="Loading discount settings..."
        onOpenChange={handleClose}
      />
    );
  }

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-120">
        <DialogHeader className="space-y-1">
          <DialogTitle className="h2 font-heading text-foreground text-center leading-9">
            Edit Discounts Settings
          </DialogTitle>
          <DialogDescription className="body text-foreground text-center leading-6">
            Configure how discounts are applied in POS.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="limitDiscountsToOrder"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex flex-row justify-between">
                        <Switch
                          checked={field.value}
                          className="mr-2 items-center justify-start"
                          id="limitDiscountsToOrder"
                          onCheckedChange={field.onChange}
                        />
                        <div className="flex flex-col">
                          <Label htmlFor="limitDiscountsToOrder">
                            Limit discounts to 1 per order
                          </Label>
                          <p className="body-sm text-muted-foreground">
                            Up to 1 discount applied per order
                          </p>
                        </div>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="limitDiscountsToItem"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex flex-row justify-between">
                        <Switch
                          checked={field.value}
                          className="mr-2 items-center justify-start"
                          disabled={form.getValues("limitDiscountsToOrder")}
                          id="limitDiscountsToItem"
                          onCheckedChange={field.onChange}
                        />
                        <div className="flex flex-col">
                          <Label
                            className={twJoin(
                              "body-sm",
                              form.getValues("limitDiscountsToOrder")
                                ? "text-muted-foreground"
                                : "text-muted-foreground"
                            )}
                            htmlFor="limitDiscountsToItem"
                          >
                            Limit discounts to 1 per item
                          </Label>
                          <p
                            className={twJoin(
                              "body-sm",
                              form.getValues("limitDiscountsToOrder")
                                ? "text-muted-foreground"
                                : "text-muted-foreground"
                            )}
                          >
                            Up to 1 discount applied per item
                          </p>
                        </div>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="enableOneTimeDiscount"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex flex-row justify-between">
                        <Switch
                          checked={field.value}
                          className="mr-2 items-center justify-start"
                          id="enableOneTimeDiscount"
                          onCheckedChange={field.onChange}
                        />
                        <div className="flex flex-col">
                          <Label htmlFor="enableOneTimeDiscount">
                            Enable one time discounts
                          </Label>
                          <p className="body-sm text-muted-foreground">
                            Each discount option can only be applied once per
                            order
                          </p>
                        </div>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button
                disabled={updateDiscountSettingsMutation.isPending}
                type="submit"
              >
                Save
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
